// NOKE vendor bucketing — MUST mirror the backend's rule exactly, because staff
// read the client-side split off the POS sticky bar and key it into the mall POS
// BEFORE our backend ever sees the sale. Backend rule: any vendor string that
// contains "mdoc" (case-insensitive, e.g. "mdoc", "MdoC", "mdocmen") buckets to
// MdoC; everything else buckets to Metanoia.

export type NokeVendorBucket = 'MdoC' | 'Metanoia';

export const vendorBucket = (vendor: string | null | undefined): NokeVendorBucket =>
  /mdoc/i.test(vendor || '') ? 'MdoC' : 'Metanoia';

// Re-bucket a vendor_totals map (client-computed or backend-returned) into the
// two display buckets, merging raw keys that land in the same bucket.
export const bucketVendorTotals = (
  totals: Record<string, number> | null | undefined
): Record<NokeVendorBucket, number> => {
  const out: Record<NokeVendorBucket, number> = { Metanoia: 0, MdoC: 0 };
  Object.entries(totals || {}).forEach(([vendor, amount]) => {
    const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? 0));
    if (!isNaN(n)) out[vendorBucket(vendor)] += n;
  });
  return out;
};

// A sale line as needed for vendor allocation (subset of the POS TicketLine).
export interface VendorAllocLine {
  vendor: string;
  unit_price: number;
  qty: number;
}

// NET per-vendor split for a ticket — MUST replicate the backend's allocation
// exactly, because staff key this number into the mall POS from the pre-submit
// sticky bar. Algorithm (largest remainder, integer NT$):
//   1. Sum gross per bucket; clamp the discount to [0, gross].
//   2. Allocate the discount across buckets proportionally to each bucket's
//      gross; take integer floors first.
//   3. Hand the remaining units (discount - sum of floors) one at a time to the
//      buckets with the largest fractional parts.
//   4. Net bucket = bucket gross - allocated discount. Buckets sum exactly to
//      gross - discount and can never go negative (a bucket's proportional
//      share never exceeds its own gross).
export const allocateVendorTotals = (
  lines: VendorAllocLine[],
  discount: number
): Record<NokeVendorBucket, number> => {
  const grossByBucket: Record<NokeVendorBucket, number> = { Metanoia: 0, MdoC: 0 };
  lines.forEach((l) => {
    grossByBucket[vendorBucket(l.vendor)] += l.unit_price * l.qty;
  });
  const gross = grossByBucket.Metanoia + grossByBucket.MdoC;

  // Clamp to [0, gross]; round to whole NT$ (all counter amounts are integers).
  const d = Math.round(Math.min(Math.max(0, discount || 0), gross));
  if (d <= 0 || gross <= 0) {
    return { ...grossByBucket };
  }

  const buckets: NokeVendorBucket[] = ['Metanoia', 'MdoC'];
  const rawShares = buckets.map((b) => (d * grossByBucket[b]) / gross);
  const floors = rawShares.map((s) => Math.floor(s));
  let remainder = d - floors.reduce((a, b) => a + b, 0);

  // Distribute the leftover units to the largest fractional parts.
  const byFraction = buckets
    .map((b, i) => ({ i, frac: rawShares[i] - floors[i] }))
    .sort((a, b) => b.frac - a.frac);
  const allocated = [...floors];
  for (let k = 0; remainder > 0; k = (k + 1) % byFraction.length) {
    allocated[byFraction[k].i] += 1;
    remainder -= 1;
  }

  return {
    Metanoia: grossByBucket.Metanoia - allocated[0],
    MdoC: grossByBucket.MdoC - allocated[1],
  };
};
